import { uiActions } from '@/core/state/ui'
import { networkDataActions, networkDataStore, DEFAULT_MAP_FILTERS, type MapFilters } from '@/core/state/networkData'
import { requestMapRender } from './mapBridge'

/**
 * The quick filter bar's actions, as plain functions (Phase 1D).
 * `useLaneManagement.js` and `useFilterHandlers.js` are shims over these until
 * the bar itself becomes React and calls them directly. Not 1E, which this
 * header used to name: 1E replaced the menu, and QuickFilterBar.vue is
 * untouched — MapView still binds four of those handlers.
 *
 * The filter set lives in the network store; the map redraw goes through the
 * bridge, so nothing here imports Leaflet.
 */

const MAP_FILTERS_KEY = 'mapFilters'

function persist(filters: MapFilters) {
  try {
    localStorage.setItem(MAP_FILTERS_KEY, JSON.stringify(filters))
  } catch {
    /* storage unavailable: in-memory only, as before */
  }
}

type NetworkRows = Parameters<typeof networkDataActions.setNetworkData>[0]

/**
 * MapView (and the optimization command) hand over the network just loaded, so
 * a filter change can redraw without them.
 *
 * The payload goes into the STORE, not a module-level `let` beside it: there
 * were three copies of the network — MapView's ref, that `let`, and the
 * store's rows — and after an optimization they disagreed, so a redraw could
 * draw a network the stat cards had already replaced.
 */
export function setRenderableNetwork(networkData: unknown) {
  networkDataActions.setNetworkData((networkData as NetworkRows) ?? null)
}

/** The network the map should draw, as last published. */
export function renderableNetwork(): unknown {
  return networkDataStore.getState().renderableNetwork
}

function redraw() {
  const network = renderableNetwork()
  if (network) requestMapRender(network, networkDataStore.getState().filters)
}

/**
 * Draw the current network with the current filters.
 *
 * Takes no arguments on purpose. Every caller used to be handed
 * `renderNetworkWithFilters` plus `fullNetworkData` and `currentFilters` to
 * pass back into it — eight composables deep in places — which is how a caller
 * could redraw with a network or a filter set that was no longer the current
 * one. Both now have a single owner, so asking for a redraw needs neither.
 */
export function redrawMap() {
  redraw()
}

/**
 * Load the saved filters over the defaults, seeding the two lists a caller
 * supplies. Mirrors useLaneManagement.initializeFilters, including its
 * fallback to defaults when the saved value is unreadable.
 */
export function initializeFilters(networkLocations: string[] = [], networkSegments: string[] = []) {
  const base: MapFilters = {
    ...DEFAULT_MAP_FILTERS,
    capacity: { ...DEFAULT_MAP_FILTERS.capacity },
    capacityCounts: { ...DEFAULT_MAP_FILTERS.capacityCounts },
    networkLocations,
    networkSegments,
  }
  try {
    const saved = localStorage.getItem(MAP_FILTERS_KEY)
    if (saved) {
      const parsed = JSON.parse(saved) as Partial<MapFilters>
      networkDataActions.replaceFilters({
        ...base,
        ...parsed,
        networkLocations: parsed.networkLocations?.length ? parsed.networkLocations : networkLocations,
        networkSegments: parsed.networkSegments?.length ? parsed.networkSegments : networkSegments,
        capacity: { ...base.capacity, ...(parsed.capacity ?? {}) },
        capacityCounts: { ...base.capacityCounts, ...(parsed.capacityCounts ?? {}) },
      })
      return
    }
  } catch {
    /* a corrupt saved set is ignored, as before */
  }
  networkDataActions.replaceFilters(base)
}

/** Merge a change, persist it, redraw the map. */
export function applyFilters(partial: Partial<MapFilters> | null | undefined) {
  networkDataActions.mergeFilters(partial)
  persist(networkDataStore.getState().filters)
  redraw()
}

/** Back to defaults, keeping the two lists the caller supplies. */
export function resetFilters(networkLocations: string[] = [], networkSegments: string[] = []) {
  networkDataActions.replaceFilters({
    ...DEFAULT_MAP_FILTERS,
    capacity: { ...DEFAULT_MAP_FILTERS.capacity },
    capacityCounts: { ...DEFAULT_MAP_FILTERS.capacityCounts },
    networkLocations,
    networkSegments,
  })
  persist(networkDataStore.getState().filters)
  redraw()
}

/** The capacity tallies the bar shows. Not persisted: they are derived from the network. */
export function updateCapacityCounts(counts: Partial<MapFilters['capacityCounts']>) {
  networkDataActions.mergeFilters({ capacityCounts: counts as MapFilters['capacityCounts'] })
}

/** Show or hide the bar. Hiding it also un-collapses, so it comes back expanded. */
export function setQuickFilterBarVisible(visible: boolean) {
  uiActions.setQuickFilterBar(visible)
}

export function setQuickFilterCollapsed(collapsed: boolean) {
  uiActions.setQuickFilterCollapsed(collapsed)
}

/** What the bar's own hide button does. */
export function hideQuickFilterBar() {
  setQuickFilterBarVisible(false)
}
